import { browserTimeZoneQuery } from "./timeZone";
import type { AutodeskSavedForm } from "../../../backend/src/shared/autodeskSavedForm";
import type { AutodeskDailyLogFieldTarget, AutodeskDailyLogMappingPlan } from "../../../backend/src/shared/autodeskFormTemplate";
import { DATA_CHANGED_EVENT } from "./resourceCache";
import type { AskResultSet } from "../../../backend/src/shared/askRecordTools";
import type { AutodeskSubtypeOption } from "../../../backend/src/shared/autodeskSubtypeOptions";
/**
 * The console SPA's view of `GET /api/items`.
 *
 * These interfaces mirror `src/server/index.ts`'s response, with one
 * deliberate difference: every `Date` on a `ConsoleItem` arrives as an ISO
 * STRING, because that is what `JSON.stringify` produces. Declaring them as
 * `Date` here — by importing the server's `ConsoleItem` directly — would
 * typecheck and then fail at runtime the first time anything called
 * `.getTime()`. The kind/status unions ARE imported from the server, so a
 * new record kind is a compile error here rather than a row that silently
 * renders as "unknown".
 */

import { authHeaders, hasAuthAccessor } from "./auth/token";
import type {
  ConsoleAssigneeCandidate,
  ConsoleAssigneeSuggestion,
  ConsoleItemKind,
  ConsoleItemStatus,
  ConsoleManpowerMerge,
  ConsoleSyncState,
} from "../server/projection/consoleItem";
import type { ConsoleGroupBy } from "../server/queries/listItems";
import type { AutodeskReviewDestinations } from "../server/queries/getReviewQueue";
import type { ProjectPickerByIdResponse, ProjectPickerPage } from "../server/queries/projectPicker";
import type { AdoptionStatTarget } from "../server/queries/getAdoption";
import type { Workspace } from "../server/auth";
import type { AutodeskDailyReportOverrides, ReviewItemStatus } from "../../../backend/src/shared/types";
import type {
  ProcoreDailyLogFieldValue,
  ProcoreDailyLogScope,
} from "../../../backend/src/shared/procoreDailyLog";
import type { ItemEventType } from "../../../backend/src/backend/services/itemMovement";
import type { ItemEventActor } from "../../../backend/src/backend/models/item-event.model";
import type {
  NudgeChannel,
  PreparedNudge,
} from "../server/queries/prepareNudge";
import type {
  ReviewAssignment,
  ReviewKindTab,
  ReviewProjectTab,
  ReviewWriteTarget,
} from "../server/queries/getReviewQueue";
import type { DailyLogReportCategory } from "../../../backend/src/shared/reportFields";

export type {
  AdoptionStatTarget,
  ReviewAssignment,
  ReviewKindTab,
  ReviewProjectTab,
  ReviewWriteTarget,
  ConsoleAssigneeCandidate,
  ConsoleAssigneeSuggestion,
  ConsoleItemKind,
  ConsoleItemStatus,
  ConsoleManpowerMerge,
  ConsoleGroupBy,
  Workspace,
  ReviewItemStatus,
  ItemEventType,
  ItemEventActor,
  NudgeChannel,
  PreparedNudge,
  DailyLogReportCategory,
};

/** The Ask search horizon. Absence and unknown legacy URL values are broad. */
export type AskTimeHorizon = "all" | "today" | "week" | "month";

/**
 * Read the current console URL once at the caller. Every Ask entry point uses
 * this same parser, so no composer can quietly regain a narrower default.
 */
export function askTimeHorizon(search: string): AskTimeHorizon {
  const query = search.startsWith("?") ? search.slice(1) : search;
  const value = new URLSearchParams(query).get("askRange");
  return value === "today" || value === "week" || value === "month" || value === "all"
    ? value
    : "all";
}

export interface ConsoleRow {
  additionalAssignees?: { remoteId: string; name: string }[];
  id: string;
  kind: ConsoleItemKind;
  jobSiteId: string;
  visitId: string;
  title: string;
  description?: string;
  summary?: string;
  category?: string;
  /** A task's Procore category (Administrative, Closeout, …). Task only. */
  taskCategory?: string;
  recordType?: ProcoreDailyLogScope;
  fieldValues?: Record<string, ProcoreDailyLogFieldValue>;
  autodeskFieldTarget?: AutodeskDailyLogFieldTarget;
  autodeskReferences?: Record<string, { id: string; label?: string }>;
  /** The project's linked integration; determines which review requirements apply. */
  syncSource?: "procore" | "autodesk";
  location?: { name: string; path?: string; freeText?: boolean };
  severity?: "low" | "medium" | "high";
  workflowStatus?: string;
  dueDate?: string;
  trade?: string;
  status: ConsoleItemStatus;
  reviewStatus: ReviewItemStatus;
  ballHolder?: string;
  /** ISO string — see the file header. */
  ballSince?: string;
  /** The RESOLVED assignee — a display name and the company they work for,
   *  present only when the record genuinely carries an assignee. Never the
   *  raw stored holder string; see `resolveAssignee` on the server. */
  assigneeName?: string;
  assigneeCompany?: string;
  assigneeCompanySource?: "directory" | "email-domain" | "assignee-name";
  /** Who the AI thought this belonged to, and how sure it was — the ranked
   *  candidates the Assign-to modal draws. Absent when no directory
   *  resolution ran, which is also how the picker tells a project with no
   *  Procore/Autodesk link (free text IS the assignee) from one where a
   *  directory was searched and nobody was settled on. */
  assigneeSuggestion?: ConsoleAssigneeSuggestion;
  /** The assignee the walk named that the directory search found nobody for
   *  (LED-1155's `No "Brian Smith" in directory`). */
  assigneeHeardName?: string;
  /** A manpower entry's company match, in the assignee match's shape. */
  companySuggestion?: ConsoleAssigneeSuggestion;
  /** The manpower company the walk named that the directory lacks. */
  companyHeardName?: string;
  /** A manpower entry colliding with a row on the Procore log (LED-1156). */
  manpowerMerge?: ConsoleManpowerMerge;
  assigneeDirectory?: boolean;
  workspaceAssignee?: import("../../../backend/src/shared/workspaceAssignees").WorkspaceAssignee;
  /** ISO string — see the file header. */
  lastMovementAt: string;
  /** ISO string — see the file header. */
  createdAt: string;
  /** A PRESIGNED R2 GET, or absent. `listItems` signs it; what the document
   *  stores is a `backend/` path the console must not fetch, so a reference
   *  that cannot be resolved arrives as nothing at all rather than as a URL
   *  that would only break on load. */
  thumbnailUrl?: string;
  /** Up to three of the record's photos, each a PRESIGNED R2 GET — the hover
   *  peek and the board card draw these without reading the record. */
  previewPhotos?: Array<{ url: string; capturedAt?: string }>;
  /** How many photos or videos the record carries — the observations table's
   *  camera column. Counted over the same presentable-media filter that
   *  chooses `thumbnailUrl`, so the count and the picture agree. */
  photoCount?: number;
  /** Procore's human-facing Punch number — never its opaque API id or
   * LedAI/Procore reference. Punch only, and only once captured from Procore. */
  punchNumber?: string;
  procoreSync?: ConsoleSyncState;
  daysSinceMovement: number;
  stalled: boolean;
}

/** A project directory choice. `remoteId`, never its display name, is what
 * the mobile manager needs to resolve and synchronize a selected reference. */
export interface ItemEditDirectoryOption {
  additionalAssignees?: { remoteId: string; name: string }[];
  remoteId: string;
  name: string;
  source?: "ledai" | "procore" | "autodesk" | "files";
  providerRemoteIds?: string[];
  path?: string;
  /** A location's parent in the provider's tree (ACC, a files catalog). */
  parentRemoteId?: string;
  email?: string;
  company?: string;
  companyRemoteId?: string;
  tradeRemoteId?: string;
  tradeName?: string;
  jobTitle?: string;
}

export interface ItemEditOptions {
  locations: ItemEditDirectoryOption[];
  observationTypes: string[];
  /** ACC's Type › Subtype catalog — only when asked for (`fetchAutodeskSubtypes`). */
  autodeskSubtypes?: AutodeskSubtypeOption[];
  assignees: ItemEditDirectoryOption[];
  companies: ItemEditDirectoryOption[];
  trades: ItemEditDirectoryOption[];
}

export interface ConsoleGroup {
  label: string;
  rows: ConsoleRow[];
}

export interface ConsoleSite {
  id: string;
  name: string;
  /** "City, ST" from the project's address, when it has one. */
  city?: string;
  /** Street address and coordinates — the Home tiles' site map. */
  address?: string;
  location?: { lat: number; lng: number };
  syncSource?: "procore" | "autodesk";
}

/**
 * The workspace envelope every view route carries, empty view or not.
 *
 * It is what lets an empty panel say WHICH workspace is empty. A view that
 * could not name its workspace would make "nothing in this team" and "the
 * console is broken" look identical, which is the one failure this whole
 * service exists to remove.
 */
export interface WorkspaceEnvelope {
  /** The caller the server actually authenticated as — the console prints it
   *  so "who am I signed in as" is never a guess. */
  userId: string;
  /**
   * WHO that id is: the caller's own row in the Clerk `User` mirror, resolved
   * server-side on every route by the SAME `resolveCallerIdentity` the Brief's
   * greeting and My Work use.
   *
   * Always present, because the question always has an answer: a caller with
   * no mirror row is `{ userId }` and nothing more — which, measured against
   * the staging database, is what EVERY row is: the mirror carries `userId`,
   * `clerkUserId` and integration tokens, and no name or email at all,
   * because `upsertUserFromClerk` only ever runs from the Clerk webhook.
   *
   * So this field is a FALLBACK, not the answer. The sidebar reads the
   * signed-in person from Clerk in the browser, which has held the profile
   * all along, and falls back to this for the UNGATED console (no
   * publishable key), which has no Clerk to ask. `auth/identity.tsx`'s
   * `resolveIdentity` is where the order is written down.
   */
  caller: CallerIdentity;
  activeWorkspace: Workspace;
  availableWorkspaces: Workspace[];
}

import type { ConsoleSort } from "../server/projection/priority";
import { consoleUrl } from "./basePath";
import type { ConsoleSeverity } from "../server/queries/listItems";

export interface ItemsResponse extends WorkspaceEnvelope {
  groupBy: ConsoleGroupBy;
  stalledOnly: boolean;
  /** The order the SERVER put these rows in. Echoed back so the control can
   *  show what is actually in effect rather than what the URL asked for —
   *  the two differ when `?sort=` is absent and the server applies its
   *  default. */
  sort: ConsoleSort;
  /** The severities in force, or `null` for "every severity". Echoed so the
   *  chip shows what the SERVER filtered on rather than what the URL asked
   *  for. */
  severities: ConsoleSeverity[] | null;
  /** How many records in scope still await review — the sidebar's Review
   *  badge. Counted server-side over the whole scope, so a filter on this
   *  page never changes it. */
  pendingReviewCount: number;
  /** Records set aside for later in the same scope — still reviewable. */
  savedForLaterCount?: number;
  sites: ConsoleSite[];
  groups: ConsoleGroup[];
  /**
   * How many walks are in scope — the number the sidebar's All Walks row
   * prints, counted by `countWalks` over the same ceiling `/api/walks` reads
   * through.
   *
   * It rides on THIS response rather than costing the console a second
   * request for one integer. Optional on the wire: a console talking to a
   * server that predates it prints `—`, exactly as every other row does when
   * its count is unavailable, instead of inventing a number.
   */
  walkCount?: number;
  /** Workspace-wide presentable photo/video total for the Photos nav row.
   * Optional keeps a newer console compatible with a server that predates
   * the sidebar total. */
  photoCount?: number;
  /** All projects only: walks and photos per project, for the project
   *  picker (`server/queries/siteActivity.ts`). Absent with a project in
   *  scope, and from a server that predates it. */
  siteActivity?: Record<string, import("../server/queries/siteActivity").SiteActivity>;
}

// ---------------------------------------------------------------------------
// GET /api/brief
// ---------------------------------------------------------------------------

export interface BallLoad {
  holder: string;
  count: number;
}

export interface ReviewKindCount {
  kind: ConsoleItemKind;
  count: number;
}

/** Mirrors `server/queries/getBrief.ts`'s `BriefGeneratedCounts`. */
export interface GeneratedKindCount {
  kind: ConsoleItemKind;
  count: number;
}

export interface BriefEvidenceRow {
  itemId: string;
  kind: ConsoleItemKind;
  jobSiteId: string;
  title: string;
  /** A presigned R2 GET, or `null` — the tile renders either way. */
  thumbnailUrl: string | null;
  /** ISO string. */
  capturedAt: string;
}

/** Mirrors `server/queries/getAdoption.ts`. Dates arrive as ISO strings — the
 *  client must not reuse the server type, whose `Date` fields would break at
 *  the first `.getTime()`. */
export interface AdoptionSeatRow {
  userId: string;
  name: string;
  init: string;
  role: string;
  email: string | null;
  nameCollides: boolean;
  walks: number;
  recordedMs: number;
  /** Of `walks`, the ones that started today. A `walked` seat with zero of
   *  these stopped; a `walked` seat with some is still out. The week cannot
   *  tell those two apart, which is why the roster is not drawn from `state`
   *  alone. */
  todayWalks: number;
  todayRecordedMs: number;
  state: "walked" | "desk" | "dormant";
  barPercent: number;
  /** Today's share of the same scale — the darker inner segment. Never
   *  greater than `barPercent`. */
  todayBarPercent: number;
  note: string;
  /** Project on this member's latest in-scope walk, if they have one. */
  lastWalkProject: string | null;
}

/** The day so far — the panel's primary read. Mirrors `AdoptionToday`;
 *  `startedAt` arrives as an ISO string. */
export interface AdoptionTodayRow {
  startedAt: string;
  seatsOut: number;
  walks: number;
  recordedMs: number;
  names: string[];
  quiet: boolean;
  headline: string;
  detail: string;
}

export interface AdoptionResponse extends WorkspaceEnvelope {
  hasTeam: boolean;
  rosterUnavailable: boolean;
  windowDays: number;
  today: AdoptionTodayRow;
  seats: AdoptionSeatRow[];
  stats: Array<{
    value: string;
    label: string;
    note: string;
    tone: "ink" | "green" | "amber";
    /** Where the tile goes when clicked — named by the server (spec §2.1). */
    target: AdoptionStatTarget;
  }>;
  split: Array<{
    label: string;
    count: number;
    percent: number;
    names: string[];
    tone: "ink" | "gold" | "red";
  }>;
  coverage: Array<{
    siteId: string;
    name: string;
    lastWalkAt: string | null;
    daysSinceWalk: number | null;
    last: string;
  }>;
}

/** Mirrors `server/queries/getBrief.ts`'s `SubsBehindItem`. */
export interface SubsBehindItemRow {
  itemId: string;
  kind: ConsoleItemKind;
  title: string;
  jobSiteId: string;
  assignee: string;
  daysSinceMovement: number;
}

/** Mirrors `server/queries/getBrief.ts`'s `SubsBehindCompany`. */
export interface SubsBehindCompanyRow {
  company: string;
  source: "directory" | "email-domain" | "assignee-name";
  count: number;
  worstDays: number;
  jobSiteIds: string[];
  items: SubsBehindItemRow[];
}

export interface BriefResponse extends WorkspaceEnvelope {
  sites: ConsoleSite[];
  /** ISO string — the instant every stall verdict below was computed against. */
  generatedAt: string;
  /** The stall line these rows were judged against, in days. Printed rather
   *  than assumed — a constant on the client would be a second place that can
   *  disagree with org policy. */
  stallThresholdDays: number;
  /** Non-terminal rows in scope, held or not. */
  openItems: number;
  stalled: ConsoleRow[];
  /** Exact totals when Home opts into bounded preview arrays. */
  stalledCount?: number;
  movedCount?: number;
  /** Most recently moved first, as the server ordered it. */
  moved: ConsoleRow[];
  /** The window `moved` was computed with, in days — the org's stall line
   *  unless the caller overrode it. Printed, never assumed. */
  movedWithinDays: number;
  ballDistribution: BallLoad[];
  /** Companies with past-due items, biggest pile first — the Brief's
   *  "By trade" column and "subs behind" stat, with per-item traceability. */
  subsBehind: SubsBehindCompanyRow[];
  /** Still returned, and NOT rendered by the Brief (spec §2.3) —
   *  `getProjectHome` reads it. */
  reviewCounts: { byKind: ReviewKindCount[]; total: number };
  /** Records that exist in scope, by kind. The Brief's own panel. */
  generated: { byKind: GeneratedKindCount[]; total: number };
  recentEvidence: BriefEvidenceRow[];
}

// ---------------------------------------------------------------------------
// GET /api/my-work
// ---------------------------------------------------------------------------

/** Who the console decided "me" is. Reported rather than implied: an empty
 *  "assigned to me" means something different depending on whether the
 *  caller resolved to an email or to an opaque id nothing will ever match,
 *  and the view says which. */
export interface CallerIdentity {
  userId: string;
  email?: string;
  name?: string;
}

export interface MyWorkResponse extends WorkspaceEnvelope {
  sites: ConsoleSite[];
  generatedAt: string;
  identity: CallerIdentity;
  notReviewed: ConsoleRow[];
  assignedToMe: ConsoleRow[];
  waitingOnOthers: ConsoleRow[];
}

// ---------------------------------------------------------------------------
// GET /api/items/:itemId
// ---------------------------------------------------------------------------

/**
 * One photo or video. There is no `duration` and no field that could carry
 * one — `getItemDetail` drops every non-image/video media row structurally
 * (spec §7.3), and no view may reintroduce one.
 *
 * `url` / `thumbnailUrl` are presigned R2 GETs, or `null` when R2 is
 * unconfigured. A null URL renders as a missing thumbnail; it never throws.
 */
export interface ItemEvidenceRow {
  id: string;
  type: "image" | "video";
  url: string | null;
  thumbnailUrl: string | null;
  caption?: string;
  /** Contextual photo description, including any user edit. */
  description?: string;
  /** The photo's own location, when it has a named one. */
  location?: { name: string; path?: string; procoreLocationId?: string };
  /** ISO string. */
  capturedAt: string;
}

export interface ItemHistoryRow {
  id: string;
  type: ItemEventType;
  /** ISO string. */
  at: string;
  actor?: ItemEventActor;
  isBallChange: boolean;
  ballHolder?: string;
  isMovement: boolean;
  payload?: Record<string, unknown>;
}

/** An Autodesk record's sync, as the record page shows it. A walk's
 *  daily-report Form carries `status`/`syncedAt` only; an Issue also its
 *  number, ACC link, failure reason and subtype (`getItemDetail`). */
export interface AutodeskIssueSync {
  status?: string;
  syncedAt?: string;
  number?: number;
  url?: string;
  error?: string;
  subtypeId?: string;
}

export interface ItemDetailResponse extends WorkspaceEnvelope {
  integrationSync?: {
    procore?: { status?: string; syncedAt?: string };
    autodesk?: AutodeskIssueSync;
  };
  item: ConsoleRow;
  evidence: ItemEvidenceRow[];
  /** Oldest first. `[]` means nothing has happened yet — a real answer, not
   *  a failure: the route throws on every failure path. */
  history: ItemHistoryRow[];
  /** The record's owner. `name` only when someone could name them. */
  creator?: { userId: string; name?: string };
  siteName?: string;
}

// ---------------------------------------------------------------------------
// GET /api/daily-log
// ---------------------------------------------------------------------------

export interface DailyLogCard {
  category: DailyLogReportCategory;
  label: string;
  rows: ConsoleRow[];
}

export interface DailyLogQuietCategory {
  category: DailyLogReportCategory;
  label: string;
}

export interface DailyLogOutsideTaxonomy {
  category: string;
  rows: ConsoleRow[];
}

/** A walk that produced entries on the day being shown — the timeline's
 *  header. */
export interface DailyLogAutodeskTemplate {
  layoutId: string;
  formId?: string;
  projectId?: string;
  templateId?: string;
  plan: AutodeskDailyLogMappingPlan;
  formNum?: number;
  formName?: string;
  formDate?: string;
  /** The last publish's outcome; "pending" while one is running. */
  syncStatus?: "pending" | "synced" | "failed";
  syncError?: string;
  /** ISO string. */
  syncedAt?: string;
  /** ISO string — when the last publish started. */
  attemptedAt?: string;
  /** Only the walk's owner publishes, so only the owner edits the report. */
  editable?: boolean;
  overrides?: AutodeskDailyReportOverrides;
  /** Records whose table rows the last settled publish wrote to Autodesk. */
  syncedItemIds?: string[];
  /** Walk photos the Form references (the report's Photos). */
  attachmentMediaIds?: string[];
  /** Each table record's cells as the publisher writes them, by column id. */
  cells?: Record<string, Record<string, { value: string | number | boolean | null; editKey?: string }>>;
}

export interface DailyLogWalkRow {
  visitId: string;
  autodeskTemplate?: DailyLogAutodeskTemplate;
  /** Mid-publish without a template this read: keep the report last drawn. */
  autodeskPublishing?: true;
  name?: string;
  /** ISO string. */
  startedAt?: string;
  recordedMinutes?: number;
  walkerName?: string;
}

export interface DailyLogDayRow {
  /** `YYYY-MM-DD` in the caller's own offset. */
  day: string;
  entries: number;
  pendingReview: number;
  /** Distinct walks behind those entries. */
  walks?: number;
}

export interface DailyLogResponse extends WorkspaceEnvelope {
  /** The order the SERVER put these rows in. Echoed back so the control can
   *  show what is in effect rather than what the URL asked for. */
  sort: ConsoleSort;
  generatedAt: string;
  /** ISO strings. Absent means "everything ever logged". */
  range?: { from: string; to: string };
  hero: {
    sites: ConsoleSite[];
    totalItems: number;
    pendingReview: number;
    latestActivityAt?: string;
  };
  /** Every day in scope that logged something, newest first — the picker
   *  beside the report. */
  days: DailyLogDayRow[];
  /** The walks behind the day being shown, newest first. */
  walks: DailyLogWalkRow[];
  /** How many walks the day had, whether or not any of them logged. Equal to
   *  `walks.length` except on an empty day, where it is counted from the
   *  visits — see `getDailyLog`. */
  walksThatDay: number;
  /** Canonical order, non-empty only. */
  cards: DailyLogCard[];
  /** Canonical order. Together with `cards`, a PARTITION of the taxonomy —
   *  a category with nothing logged is named here, never dropped and never
   *  an empty card. */
  quietCategories: DailyLogQuietCategory[];
  outsideTaxonomy: DailyLogOutsideTaxonomy[];
  /** ISO instant of the most recent entry BEFORE the day being shown, absent
   *  when nothing in scope precedes it. The empty day's way out — see
   *  `getDailyLog`'s own note on why `hero.latestActivityAt` cannot answer
   *  this (it is computed from the rows inside the range). */
  lastLoggedBefore?: string;
}

// ---------------------------------------------------------------------------
// GET /api/items/:itemId/nudge
// ---------------------------------------------------------------------------

export interface NudgeResponse extends WorkspaceEnvelope {
  item: ConsoleRow;
  nudge: PreparedNudge;
}

// ---------------------------------------------------------------------------
// GET /api/people
// ---------------------------------------------------------------------------

export interface PersonSiteRef {
  id: string;
  name: string;
}

/**
 * One member of the current organization.
 *
 * Note what is NOT here: no presence, no channel, no "on LedAI". This console
 * knows nothing about who is online, and there are no partner accounts at all
 * — every partner is external (spec §3.6). See `server/queries/getPeople.ts`
 * for why field / office / partners was replaced by one roster.
 */
/** One member's output, by kind. Photos are counted but are NOT records —
 *  see the server's own note on why the two are never added together. */
export interface PersonRecordCounts {
  log: number;
  observation: number;
  task: number;
  punch: number;
  photo: number;
}

export interface PeopleMemberRow {
  userId: string;
  name: string;
  /** `null` when Clerk has none. Shown in place of the role when two members
   *  render the same display name, which really happens. */
  email: string | null;
  init: string;
  /** What Clerk literally returned — `org:admin`, `org:member`, a custom role. */
  rawRole: string;
  role: OrgRole;
  isYou: boolean;
  nameCollides: boolean;
  walks: number;
  minutes: number;
  itemsProduced: number;
  /** What those records were, by kind, plus the photos the walks captured.
   *  The Team table draws a column per entry — see `getPeople.ts`. */
  records: PersonRecordCounts;
  /** ISO string. Latest walk in this workspace across all time, independent of the count window. */
  lastWalkAt?: string;
  sites: PersonSiteRef[];
  /** ISO string, or `null` when the seat has NEVER been signed into. */
  lastSignInAt: string | null;
}

export interface PeopleResponse extends WorkspaceEnvelope {
  generatedAt: string;
  withinDays: number;
  /** False in a Personal workspace, which has no organization at all. */
  hasOrg: boolean;
  /** Present exactly when there is no roster to show, saying WHICH reason. */
  rosterUnavailableReason?: string;
  members: PeopleMemberRow[];
}

// ---------------------------------------------------------------------------
// GET /api/people/:userId
// ---------------------------------------------------------------------------

/**
 * One member's usage — the console's own answer to the question `backend`'s
 * internal admin dashboard answers for a whole environment.
 *
 * `server/queries/getMemberUsage.ts` records why `/dashboard-metrics/user/:id`
 * is mirrored rather than proxied, and which of its sections are deliberately
 * missing here (device, settings, Procore connection, `withAssignee`).
 */
export interface MemberUsageIdentityRow {
  userId: string;
  name: string;
  email: string | null;
  init: string;
  rawRole: string;
  role: OrgRole;
  isYou: boolean;
  /** ISO string, or `null` when the seat has NEVER been signed into. */
  lastSignInAt: string | null;
}

export interface MemberUsageTotalsRow {
  walks: number;
  recordedMs: number;
  recordedMinutes: number;
  records: number;
}

export interface MemberUsageTrendRow {
  /** `YYYY-MM-DD`, UTC. */
  date: string;
  walks: number;
  recordedMinutes: number;
  records: number;
}

export interface MemberUsageCoverageRow {
  kind: ConsoleItemKind;
  total: number;
  /** How many of them name WHERE. There is no `withAssignee` counterpart —
   *  see `getMemberUsage.ts`. */
  withLocation: number;
}

export interface MemberUsageWalkRow {
  id: string;
  jobSiteId: string;
  siteName: string;
  /** May be `""` — the view supplies the placeholder, never the server. */
  name: string;
  /** ISO string. */
  startedAt: string;
  endedAt?: string;
  /** Absent, not `0`, when nothing measurable has been recorded yet. */
  recordedMinutes?: number;
  status: string;
  itemCount: number;
}

export interface MemberUsageResponse extends WorkspaceEnvelope {
  generatedAt: string;
  withinDays: number;
  hasOrg: boolean;
  /** Present exactly when there is no member to report on, saying WHICH
   *  reason. `member` is then `null`. */
  unavailableReason?: string;
  member: MemberUsageIdentityRow | null;
  totals: MemberUsageTotalsRow;
  trend: MemberUsageTrendRow[];
  trendTimeZone: "UTC";
  recordCoverage: MemberUsageCoverageRow[];
  walks: { total: number; truncated: boolean; items: MemberUsageWalkRow[] };
}

// ---------------------------------------------------------------------------
// GET /api/policies
// ---------------------------------------------------------------------------

export type OrgRole = "admin" | "member";
export type PolicyValue = boolean | string | number | string[];
export type PolicySource = "locked" | "policy-default" | "user" | "unset";

export interface PolicyChildRow {
  key: string;
  policyKey: string;
  label: string;
  type: "toggle" | "choice" | "checks" | "text" | "stepper" | "info";
  options?: string[];
  optionLabels?: Record<string, string>;
  /** Present only for the Personal workspace's mobile-settings mirror. */
  settingKey?: string;
  defaultValue: PolicyValue;
  value: PolicyValue | undefined;
  source: PolicySource;
  isSet: boolean;
  enforced: boolean;
  unavailableReason?: string;
}

export interface PolicyGroupRow {
  id: string;
  group: string;
  name: string;
  summary: string;
  locked: boolean;
  children: PolicyChildRow[];
}

export interface PoliciesResponse extends WorkspaceEnvelope {
  surface: "organization-policy" | "personal-settings";
  orgId: string | null;
  unavailableReason?: string;
  canEdit: boolean;
  callerRole: OrgRole | null;
  roleUnavailable?: boolean;
  readOnlyReason?: string;
  groups: PolicyGroupRow[];
  groupCount: number;
  childCount: number;
  lockedGroupCount: number;
  version: number;
  /** ISO string. */
  pushedAt?: string;
  pushedBy?: { userId: string; displayName?: string };
  /** ISO string. */
  lastEditedAt?: string;
  unpushedChanges: boolean;
  /** Whether this console has anywhere to SEND a change. A control that is
   *  live-looking with no backend configured would report a company-wide
   *  change that never left the browser. */
  backendConfigured: boolean;
}

// ---------------------------------------------------------------------------
// GET /api/invite
// ---------------------------------------------------------------------------

export interface OrgMemberRow {
  userId: string;
  name: string;
  email?: string;
  rawRole: string;
  role: OrgRole;
}

export interface OrgInvitationRow {
  id: string;
  email: string;
  rawRole: string;
  role: OrgRole;
  status?: string;
  /** ISO string. */
  createdAt: string;
}

export interface InviteResponse extends WorkspaceEnvelope {
  orgId: string | null;
  unavailableReason?: string;
  canInvite: boolean;
  callerRole: OrgRole | null;
  /** Whether the caller may change a member's role, remove a member, or
   *  cancel a pending invitation — see `getInvitePanel.ts`'s `canManage` doc.
   *  A DISPLAY gate only; `backend` re-verifies every write itself. */
  canManage: boolean;
  readOnlyReason?: string;
  /** Exactly `["admin","member"]`. There is no Viewer tier (spec §3.6). */
  roles: OrgRole[];
  members: OrgMemberRow[];
  membersUnavailableReason?: string;
  pending: OrgInvitationRow[];
  pendingUnavailableReason?: string;
  grantsEveryProject: true;
  backendConfigured: boolean;
}

// ---------------------------------------------------------------------------
// GET /api/search  — ⌘K
// ---------------------------------------------------------------------------

export interface SearchHitRow {
  type: "site" | "visit" | "item";
  id: string;
  title: string;
  meta: string;
  jobSiteId: string;
  kind?: ConsoleItemKind;
}

export interface SearchResponse extends WorkspaceEnvelope {
  query: string;
  sites: SearchHitRow[];
  visits: SearchHitRow[];
  items: SearchHitRow[];
  total: number;
  truncated: boolean;
}

// ---------------------------------------------------------------------------
// POST /api/ask  — Ask Led
// ---------------------------------------------------------------------------

/** The browser is the authoritative source for the reader's local calendar.
 * The server supplies the instant; Ask Led uses this IANA zone to decide what
 * "today" and "past due" mean for the person asking. */
export function askClientTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/** Where a console reader can open a cited record. Mirrors `backend`'s
 *  `AskCitationKind`. */
export type AskCitationKind =
  | "log"
  | "observation"
  | "task"
  | "punch"
  | "walk";

/** One record an answer cites. Mirrors `backend`'s `AskLedReference`.
 *
 *  `href` is deliberately absent from this mirror: it carries the MOBILE SPA
 *  route, and the console builds its own destination from `recordKind` +
 *  `recordId`/`visitId` (`components/ask/citationTarget.ts`). A field nothing
 *  reads is a field somebody eventually reads by mistake. */
export interface AskReference {
  id: string;
  kind: string;
  title: string;
  snippet: string;
  meta: string;
  recordKind: AskCitationKind;
  recordId: string;
  visitId?: string;
  siteName?: string;
  when?: string;
  /** Label for the source timestamp; older references default to Created. */
  whenLabel?: "Created" | "Started";
  /** Formatted due calendar date, independent of the user timezone. */
  dueWhen?: string;
}

export interface AskAnswer {
  conversationId?: string;
  query: string;
  answer: string;
  references: AskReference[];
  matchedDocumentCount: number;
  countedRecordCount?: number;
  matchedRecordCount?: number;
  evidenceRecordCount?: number;
  resultSets?: AskResultSet[];
  generatedAt: string;
}

/**
 * One saved conversation, from `backend`'s own store. Sidebar reads carry
 * its latest answer; opening it also fetches every saved turn.
 *
 * The whole ANSWER is carried, not a summary: re-opening a past ask has to
 * put the citations back on screen too, and a console that kept only the
 * question would have to bill the model again to show what it already knew.
 */
export interface AskHistoryEntry {
  id: string;
  query: string;
  answer: AskAnswer;
  createdAt: string;
  turns?: { id: string; query: string; answer: AskAnswer; createdAt: string }[];
}

/**
 * What `fetchAskHistory` returns.
 *
 * "You have never asked anything" and "your past asks could not be read" are
 * different facts and must look different: the first is an empty list, the
 * second carries `unavailable`. Collapsing them would let a 503 read as a
 * clean slate — the console's one failure mode, in miniature.
 */
export interface AskHistory {
  entries: AskHistoryEntry[];
  conversation?: AskHistoryEntry;
  /** Present only when the list could not be read. */
  unavailable?: string;
}

/** The phone and web share these context-aware questions; a failed optional
 * read simply returns no replacements, leaving the local fallback in place. */
export async function fetchAskSuggestions(search: string): Promise<string[]> {
  try {
    const body = await getJson<{
      ok?: boolean;
      result?: { suggestions?: unknown };
    }>(`/api/ask/suggestions${search}`);
    const suggestions = body.ok === false ? [] : body.result?.suggestions;
    return Array.isArray(suggestions)
      ? suggestions
          .filter(
            (suggestion): suggestion is string =>
              typeof suggestion === "string",
          )
          .map((suggestion) => suggestion.trim())
          .filter(Boolean)
      : [];
  } catch {
    return [];
  }
}

/** A failure the shell can render as itself rather than as an empty list —
 *  the 401/403/400/503 the route deliberately distinguishes. */
export interface ApiFailure {
  status: number;
  message: string;
}

export class ApiError extends Error implements ApiFailure {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * The console's ONE fetch.
 *
 * Every view goes through it, so every view distinguishes the same four
 * failures the routes deliberately separate (401 / 403 / 400 / 404 / 503)
 * instead of collapsing any of them into "no data". A non-OK response is
 * always an `ApiError` carrying the real status — never an empty result.
 */
async function getJson<T>(path: string): Promise<T> {
  // Mounted. At a host root this is the identity; under `ledai.build/app` it
  // is the difference between this console's API and the marketing site's
  // index page returned with a 200. See `basePath.ts`.
  path = consoleUrl(path);
  let response = await fetch(path, {
    headers: { ...(await authHeaders()), accept: "application/json" },
  });

  // A 401 is not necessarily permanent. A request can outrun the session — at
  // startup before Clerk has minted its first token, or later when one expires
  // mid-flight — and the console has no other way back: it renders "You are
  // not signed in" and nothing retries, so a fully signed-in user stares at an
  // auth error until they reload. Measured in a browser, not theorised: the
  // first `/api/items` of a signed-in session went out with no header while
  // every later one carried it.
  //
  // Retried ONCE, and only when an accessor exists — a genuinely signed-out
  // caller has nothing to retry with and must still see the 401 promptly.
  if (response.status === 401 && hasAuthAccessor()) {
    const retryHeaders = await authHeaders();
    if (retryHeaders.authorization) {
      response = await fetch(path, {
        headers: { ...retryHeaders, accept: "application/json" },
      });
    }
  }

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      // Non-JSON error body — keep the status-only message.
    }
    throw new ApiError(response.status, message);
  }

  return (await response.json()) as T;
}

/** Startup routing needs the verified workspace, not the complete item list. */
export function fetchWorkspace(search: string): Promise<Pick<ItemsResponse, "activeWorkspace">> {
  return getJson(`/api/workspace${search}`);
}

/**
 * Fetches the item list for the CURRENT URL's console state.
 *
 * The `workspace` parameter is forwarded verbatim from the page URL rather
 * than remembered in memory: it is part of what page you are on, so a
 * reload, a bookmark and a pasted link must all resolve the same workspace
 * (see `src/server/auth.ts`). Every fetcher below does the same, for the same
 * reason — a view that dropped `?workspace=` would silently answer for the
 * caller's default workspace while the page said otherwise.
 */
export interface HomeResponse {
  inventory: ItemsResponse;
  brief: BriefResponse;
  projects: ProjectCardsResponse;
  walks: WalksResponse;
  streakWalks?: WalksResponse;
}

export async function fetchHome(search: string): Promise<HomeResponse> {
  const { inventory, brief, projects, walks, streakWalks, ...envelope } = await getJson<HomeResponse & WorkspaceEnvelope>(`/api/home${search}`);
  return { inventory: { ...envelope, ...inventory }, brief: { ...envelope, ...brief },
    projects: { ...envelope, ...projects }, walks: { ...envelope, ...walks },
    ...(streakWalks ? { streakWalks: { ...envelope, ...streakWalks } } : {}) };
}

export function fetchItems(search: string): Promise<ItemsResponse> {
  return getJson<ItemsResponse>(`/api/items${search}`);
}

export function fetchBrief(search: string): Promise<BriefResponse> {
  const params = new URLSearchParams(search);
  params.set("preview", "3");
  return getJson<BriefResponse>(`/api/brief?${params}`);
}

/**
 * The adoption Brief's roster. A separate request from the Brief on purpose:
 * it is the only read that calls Clerk on the request path, so a slow or
 * unreachable Clerk degrades one panel rather than the whole page.
 */
export function fetchAdoption(search: string): Promise<AdoptionResponse> {
  // The panel's primary read is TODAY, and a calendar day has a timezone in
  // it. The server runs in UTC and the crew does not, so the browser — the one
  // party that actually knows — says where midnight falls. An OFFSET, not an
  // instant: `now` stays the server's, so this can move the day boundary and
  // nothing else. See `handleAdoption`.
  const params = new URLSearchParams(search);
  params.set("tz", String(-new Date().getTimezoneOffset()));
  return getJson<AdoptionResponse>(`/api/adoption?${params.toString()}`);
}

/**
 * The caller's recent Ask Led exchanges, from `backend`'s own store.
 *
 * The ONE fetcher here that does not throw on failure, deliberately. Every
 * other view IS its data, so a failed read must replace the page rather than
 * empty it. History is not the Ask tab — it is a list beside a composer that
 * works with or without it — and a thrown `ApiError` here would take down a
 * composer that has nothing wrong with it. The failure is still reported, in
 * `unavailable`, and the tab prints it: absent history and unreadable history
 * do not look alike.
 */
export async function fetchAskHistory(search: string, strict = false): Promise<AskHistory> {
  try {
    const body = await getJson<{
      ok?: boolean;
      error?: string;
      result?: { entries?: AskHistoryEntry[]; conversation?: AskHistoryEntry };
    }>(`/api/ask/history${search}`);

    if (body.ok === false) {
      if (strict) throw new ApiError(503, body.error ?? "Your recent asks could not be read.");
      return {
        entries: [],
        unavailable: body.error ?? "Your recent asks could not be read.",
      };
    }
    const entries = body.result?.entries;
    return { entries: Array.isArray(entries) ? entries : [], conversation: body.result?.conversation };
  } catch (error) {
    if (strict) throw error;
    return {
      entries: [],
      unavailable:
        error instanceof ApiError
          ? error.message
          : "Your recent asks could not be read from this console.",
    };
  }
}

export function fetchMyWork(search: string): Promise<MyWorkResponse> {
  return getJson<MyWorkResponse>(`/api/my-work${search}`);
}

export function fetchDailyLog(search: string): Promise<DailyLogResponse> {
  // The caller's timezone and legacy UTC offset ride along so the day PICKER buckets by the
  // reader's day — see `getDailyLog` on why a server cannot answer that from
  // its own clock.
  const joined = search.includes("?") ? "&" : "?";
  return getJson<DailyLogResponse>(
    `/api/daily-log${search}${joined}${browserTimeZoneQuery()}`,
  );
}

export function fetchItemDetail(
  itemId: string,
  search: string,
): Promise<ItemDetailResponse> {
  return getJson<ItemDetailResponse>(
    `/api/items/${encodeURIComponent(itemId)}${search}`,
  );
}

/** Tasks can retain their source note's category (for example observations).
 * Their editable fields still need task directories, including companies. */
export function itemEditCategory(item: Pick<ConsoleRow, "kind" | "category">): string {
  return item.kind === "task" ? "task" : item.category ?? item.kind;
}

/** The same project-scoped Procore directories used by the mobile Edit sheet.
 * The server verifies the site against the active workspace before forwarding
 * this read, so a URL parameter cannot broaden what the picker can see. */
export async function fetchItemEditOptions(
  jobSiteId: string,
  category: string | undefined,
  search: string,
  visitId?: string | null,
): Promise<ItemEditOptions> {
  const params = new URLSearchParams(search);
  params.set("jobSiteId", jobSiteId);
  if (visitId) params.set("visitId", visitId);
  if (category) params.set("category", category);
  const body = await getJson<{ ok: boolean; result?: ItemEditOptions; error?: string }>(
    `/api/item-edit-options?${params.toString()}`,
  );
  if (!body.ok || !body.result) throw new ApiError(503, body.error ?? "Item edit options are unavailable.");
  return body.result;
}

/** The project's ACC issue Types and Subtypes, read live by the backend as
 *  the phone's subtype picker reads them. `[]` for a project that is not on
 *  Autodesk, or when ACC could not be reached. */
export async function fetchAutodeskSubtypes(jobSiteId: string, search: string): Promise<AutodeskSubtypeOption[]> {
  const params = new URLSearchParams(search);
  params.set("jobSiteId", jobSiteId);
  params.set("autodeskSubtypes", "1");
  const body = await getJson<{ ok: boolean; result?: ItemEditOptions; error?: string }>(
    `/api/item-edit-options?${params.toString()}`,
  );
  if (!body.ok || !body.result) throw new ApiError(503, body.error ?? "Autodesk issue types are unavailable.");
  return body.result.autodeskSubtypes ?? [];
}

/** Search the project’s teammates; two-character queries also include eligible provider people. */
export async function fetchItemAssignees(
  jobSiteId: string,
  category: string | undefined,
  query: string,
  search: string,
  visitId?: string | null,
): Promise<ItemEditDirectoryOption[]> {
  const params = new URLSearchParams(search);
  params.set("jobSiteId", jobSiteId);
  if (visitId) params.set("visitId", visitId);
  if (category) params.set("category", category);
  params.set("q", query);
  const body = await getJson<{
    ok: boolean;
    result?: { assignees?: ItemEditDirectoryOption[] };
    error?: string;
  }>(`/api/item-assignees?${params.toString()}`);
  if (!body.ok || !body.result) {
    throw new ApiError(503, body.error ?? "Project assignees are unavailable.");
  }
  return body.result.assignees ?? [];
}

/** The DRAFT. There is no send — see `server/queries/prepareNudge.ts`, whose
 *  one export composes text and performs no I/O of any kind. */
export function fetchNudge(
  itemId: string,
  search: string,
  visitId?: string | null,
): Promise<NudgeResponse> {
  return getJson<NudgeResponse>(
    `/api/items/${encodeURIComponent(itemId)}/nudge${search}`,
  );
}

// ---------------------------------------------------------------------------
// GET /api/review
// ---------------------------------------------------------------------------

/**
 * One row of the review queue.
 *
 * `write` names the record to decide and the `backend/` RPC that decides it.
 * The console still performs no write itself (spec §5.3): the decision is
 * POSTed to this console's own `/api/review/:kind/:itemId`, which forwards it
 * to `backend/`'s route of the same shape under the caller's own credentials.
 * When this deployment has no backend to send it to, `writesConfigured` is
 * false and the controls render disabled with the reason beside them.
 */
export interface ReviewPhotoResponse {
  id: string;
  type: "image" | "video";
  url: string | null;
  thumbnailUrl: string | null;
  caption?: string;
  /** The text a user sees (`userCaption` over `description`); never `caption`. */
  description?: string;
  /** ISO string. */
  capturedAt: string;
}

export interface ReviewRowResponse {
  item: ConsoleRow;
  /** The walk this record came from — the "walk" half of spec §3.4's
   *  "walk + timestamp meta". Absent when the visit was never named. */
  walkName?: string;
  assignment?: ReviewAssignment;
  /** What was said on the walk that produced this record, verbatim. The
   *  caller's own words on the caller's own walk — see the server type. */
  quote?: string;
  /** The record's own media, presigned. */
  photos: ReviewPhotoResponse[];
  /** One sentence on what the console can tell is missing or uncertain.
   *  Absent when there is nothing to say. */
  flag?: string;
  write: ReviewWriteTarget;
}

/** One walk with records waiting — the review page's left column. */
export interface ReviewWalkResponse {
  visitId: string | null;
  key: string;
  jobSiteId: string;
  siteName?: string;
  walkName?: string;
  /** ISO string. */
  startedAt?: string;
  durationMinutes?: number;
  daysWaiting: number;
  byKind: Array<{ kind: ConsoleItemKind; count: number }>;
  total: number;
  decided: number;
  deferred?: number;
  acceptedIds?: string[];
  /** Autodesk projects: where this walk's daily-report records can go. */
  autodeskDestinations?: AutodeskReviewDestinations;
  /** The caller's decided records on this walk; see `ReviewDecidedRow`. */
  decidedRows?: ReviewDecidedRowResponse[];
}

/** A decided record, as the review pane folds it: what it is, what was
 *  decided, and until when that can be undone. */
export interface ReviewDecidedRowResponse {
  id: string;
  kind: ConsoleItemKind;
  category?: string;
  recordType?: string;
  title: string;
  decision: "accepted" | "rejected" | "deferred";
  /** ISO string. */
  createdAt: string;
  /** ISO string; absent when this decision can no longer be undone. */
  undoUntil?: string;
}

export interface ReviewSiteGroupResponse {
  jobSiteId: string;
  siteName?: string;
  rows: ReviewRowResponse[];
}

export interface ReviewGroupResponse {
  id: "not_reviewed" | "saved_for_later" | "reviewed";
  label: string;
  hint: string;
  total: number;
  sites: ReviewSiteGroupResponse[];
}

// ---------------------------------------------------------------------------
// GET /api/photos
// ---------------------------------------------------------------------------

export interface ConsolePhotoResponse {
  id: string;
  type: "image" | "video";
  /** Presigned R2 GETs, or `null` when R2 is not configured. Never the stored
   *  `backend/` path, which the browser cannot read. */
  url: string | null;
  thumbnailUrl: string | null;
  description?: string;
  /** ISO string. */
  capturedAt: string;
  visitId: string;
  walkName?: string;
  jobSiteId: string;
  siteName?: string;
  /** Where the photo was taken — the media item's own location. `path` is the
   *  Procore hierarchy, `name` the node. Absent when the photo has none. */
  location?: { name: string; path?: string };
}

export interface ConsolePhotoDayResponse {
  /** `YYYY-MM-DD` in the caller's own offset. */
  day: string;
  photos: ConsolePhotoResponse[];
}

export interface PhotosResponse extends WorkspaceEnvelope {
  generatedAt: string;
  sites: ConsoleSite[];
  project: string | null;
  days: ConsolePhotoDayResponse[];
  count: number;
  total?: number;
  /** Whether the cap left more behind — the gallery asks for a bigger
   *  `limit` rather than an offset, since a new walk landing mid-scroll would
   *  shift every offset under the reader. */
  hasMore: boolean;
  limit: number;
}

export function fetchPhotos(search: string): Promise<PhotosResponse> {
  // The caller's timezone and legacy UTC offset ride along, so the server groups by the day
  // the READER means — see `listPhotos` on why the server cannot decide that
  // from its own clock.
  const joined = search.includes("?") ? "&" : search ? "&" : "?";
  return getJson<PhotosResponse>(
    `/api/photos${search}${joined}${browserTimeZoneQuery()}`,
  );
}

export interface ReviewQueueResponse extends WorkspaceEnvelope {
  generatedAt: string;
  /** The caller's projects, with the provider each is linked to. */
  sites?: ConsoleSite[];
  activeKinds: ConsoleItemKind[];
  kindTabs: ReviewKindTab[];
  projectTabs: ReviewProjectTab[];
  groups: ReviewGroupResponse[];
  /** The same pending rows grouped by WALK — what the page is built from. */
  walks: ReviewWalkResponse[];
  /** Rows a person set aside — in no group, and named rather than dropped. */
  deferred: ConsoleRow[];
  deferredCount: number;
  filter: { kind: "all" | ConsoleItemKind; project: string };
  /** Whether a mutation could reach `backend/` at all from this deployment.
   *  `false` is a deployment fact, not a failure, and the view says so
   *  instead of offering controls that would quietly do nothing. */
  writesConfigured: boolean;
  /** The sentence to show beside the disabled controls. */
  writesNote: string;
}

export function fetchReviewQueue(search: string): Promise<ReviewQueueResponse> {
  return getJson<ReviewQueueResponse>(`/api/review${search}`);
}

// ---------------------------------------------------------------------------
// GET /api/projects/:jobSiteId
// ---------------------------------------------------------------------------

export interface ProjectWalkResponse {
  id: string;
  name: string;
  /** ISO string. The only time on this row.
   *
   *  Walk detail reports a walk's end and its recorded length; this row
   *  deliberately still does not. That is a density decision, not the audio
   *  rule: the Project home history answers "when was this project last
   *  walked, and what came out of it", and a per-row minute figure in a list
   *  of walks is noise. It is one field away if it turns out to be wanted. */
  startedAt: string;
  creator: string;
  creatorName?: string;
  creatorEmail?: string;
  creatorNameCollides?: boolean;
  status: string;
  itemCount: number;
}

export interface ProjectHomeResponse extends WorkspaceEnvelope {
  generatedAt: string;
  jobSiteId: string;
  siteName?: string;
  stats: {
    openItems: number;
    stalled: number;
    notReviewed: number;
    stallThresholdDays: number;
    /** ISO string, or absent when the project has never been walked. */
    lastWalkAt?: string;
  };
  stalled: ConsoleRow[];
  moved: ConsoleRow[];
  ballDistribution: BallLoad[];
  reviewCounts: { byKind: ReviewKindCount[]; total: number };
  recentEvidence: BriefEvidenceRow[];
  walks: ProjectWalkResponse[];
  walkTotal: number;
}

export function fetchProjectHome(
  jobSiteId: string,
  search: string,
): Promise<ProjectHomeResponse> {
  return getJson<ProjectHomeResponse>(
    `/api/projects/${encodeURIComponent(jobSiteId)}${search}`,
  );
}

// ---------------------------------------------------------------------------
// GET /api/walks/:visitId
// ---------------------------------------------------------------------------

/**
 * One photo or video from a walk.
 *
 * There is no `duration` and no field that could carry one, and nothing of
 * any other media type reaches this shape at all — `getWalkDetail` filters
 * through `PRESENTABLE_MEDIA_TYPES` before presigning. No view may
 * reintroduce either (spec §7.3).
 */
export interface WalkPhotoResponse {
  location?: { name: string; path?: string };
  id: string;
  type: "image" | "video";
  url: string | null;
  thumbnailUrl: string | null;
  /** The short label, when the row has one. Names a downloaded FILE; it is
   *  not what the tile prints — see `description`. */
  caption?: string;
  /** What the photo is OF, in a sentence — the field that goes into Procore.
   *  Printed under the tile and offered as its own copy; absent when no
   *  description has been generated, and NEVER filled in from `caption`. */
  description?: string;
  /** ISO string. */
  capturedAt: string;
}

export interface WalkTranscriptLineResponse {
  id: string;
  /** ISO string — a wall-clock instant, never an offset into the recording. */
  at: string;
  speakerId?: string;
  speakerLabel?: string;
  text: string;
  itemId?: string;
  itemKind?: ConsoleItemKind;
  itemTitle?: string;
}

export interface WalkSpeakerResponse {
  id: string;
  label: string;
  segmentCount: number;
}

/**
 * The walk header.
 *
 * Three temporal fields, all of them the WALK's: when it started, when it
 * ended, and how long it recorded for. A walk's length is visit metadata —
 * how long the superintendent was on site — not audio content: it reveals
 * nothing of what was said and cannot be seeked with. There is no `audioUrl`,
 * no `recordingSpans` and no audio offset anywhere on this response; and no
 * MEDIA duration on `WalkPhotoResponse`, which is a different fact about a
 * different object and stays cut (spec §7.3).
 */
export interface WalkHeaderResponse {
  nameIsUserSet?: boolean;
  updatedAt?: string;
  id: string;
  jobSiteId: string;
  siteName?: string;
  name: string;
  /** ISO string. */
  startedAt: string;
  /** ISO string. Absent while the walk is still open. */
  endedAt?: string;
  /** Recorded minutes. Absent — never `0` — when nothing measurable has been
   *  recorded yet. */
  durationMinutes?: number;
  status: string;
  creator: string;
  creatorName?: string;
  creatorEmail?: string;
  creatorNameCollides?: boolean;
}

export interface WalkDetailResponse extends WorkspaceEnvelope {
  generatedAt: string;
  walk: WalkHeaderResponse;
  /** True when the caller walked this walk. */
  viewerIsOwner: boolean;
  /** True when the transcript was WITHHELD because the caller did not walk
   *  it — the org-member rule `main_standalone` already enforces on the
   *  phone. `transcript` and `speakers` are then both empty, and the view
   *  must say why: "this walk has no transcript" would be false. */
  transcriptOwnerOnly: boolean;
  /** AI-generated walk synopsis. It is owner-only because it paraphrases the
   * owner-only transcript. */
  summary?: string;
  speakers: WalkSpeakerResponse[];
  transcript: WalkTranscriptLineResponse[];
  photos: WalkPhotoResponse[];
  items: ConsoleRow[];
  review: Omit<ReviewQueueResponse, keyof WorkspaceEnvelope>;
}

/**
 * The server already withholds a co-member's transcript — `getWalkDetail`
 * computes ownership and `redactForViewer` deletes `transcript`, `speakers`
 * and `summary` before they leave the process. This re-check is not a second
 * implementation of that rule; it is what stands between a future server
 * regression and a co-member's verbatim words rendering on screen, rather
 * than an empty transcript panel. `viewerIsOwner` is the payload's own claim
 * about who is looking, so a regression that forgets to redact still gets
 * caught here.
 *
 * Not the only seam, though: `review.groups[].sites[].rows[].quote` carries
 * the SAME kind of content — a record's own `transcriptText`, verbatim — and
 * a walk-detail payload embeds the inline review queue alongside the
 * transcript (see `WalkDetailResponse.review`). A co-member's payload can
 * legitimately carry rows with a `quote` today (the review queue's own
 * safety is "every row is the caller's own record", a DIFFERENT invariant
 * than walk ownership — see `getReviewQueue.ts`), but `viewerIsOwner: false`
 * is exactly the signal available here to strip it too, for the same reason
 * `transcript` is stripped: a future regression should produce an empty
 * quote, not a co-member's words on screen.
 *
 * Deliberately narrow beyond that: header, photos, items, and every review
 * row's non-quote fields are shared and untouched.
 */
export function fetchWalkDetail(
  visitId: string,
  search: string,
): Promise<WalkDetailResponse> {
  return getJson<WalkDetailResponse>(
    `/api/walks/${encodeURIComponent(visitId)}${search}`,
  ).then((data) => {
    if (data.viewerIsOwner) return data;
    return {
      ...data,
      transcript: [],
      speakers: [],
      summary: undefined,
      review: {
        ...data.review,
        groups: data.review.groups.map((group) => ({
          ...group,
          sites: group.sites.map((site) => ({
            ...site,
            rows: site.rows.map((row) => {
              if (row.quote === undefined) return row;
              const { quote: _quote, ...rest } = row;
              return rest;
            }),
          })),
        })),
      },
    };
  });
}

/** A short-lived URL credential for this walk's photos — see
 *  `server/downloadTicket.ts` and `lib/photoTransfer.ts`. The console asks for
 *  one when a walk page opens, because a `dragstart` cannot await it. */
export interface PhotoTicketResponse extends WorkspaceEnvelope {
  ticket: string;
  /** Epoch ms. The page re-asks rather than serving a dead URL. */
  expiresAt: number;
}

export function fetchPhotoTicket(
  visitId: string,
  search: string,
): Promise<PhotoTicketResponse> {
  return getJson<PhotoTicketResponse>(
    `/api/walks/${encodeURIComponent(visitId)}/photos/ticket${search}`,
  );
}

// ---------------------------------------------------------------------------
// GET /api/walks
// ---------------------------------------------------------------------------

/** One row of All Walks. Mirrors `server/queries/listWalks.ts`'s
 *  `ConsoleWalk`, with its `Date`s as the ISO strings the wire carries. */
export interface ConsoleWalkResponse {
  id: string;
  jobSiteId: string;
  name: string;
  /** ISO string. */
  startedAt: string;
  /** ISO string. Absent while the walk is still open. */
  endedAt?: string;
  /** Absent when the walk has recorded nothing measurable yet — which is NOT
   *  the same as zero. See `walkRecordedMinutes`. */
  recordedMinutes?: number;
  status: string;
  creator: string;
  creatorName?: string;
  creatorEmail?: string;
  creatorNameCollides?: boolean;
  itemCount: number;
  /** Records still pending review — the Review queue's own predicate. */
  pendingReviewCount: number;
  /** Records set aside for later: not pending, still to review. */
  savedForLaterCount?: number;
  /** Presentable photos/videos captured on the walk. */
  photoCount: number;
}

export interface WalksResponse extends WorkspaceEnvelope {
  sites: ConsoleSite[];
  generatedAt: string;
  /** The `?project=` this list was narrowed to, echoed back, or null. */
  project: string | null;
  walks: ConsoleWalkResponse[];
  /** How many walks are in scope in total — so a capped list can say "N of
   *  M" instead of looking complete. */
  total: number;
  limit: number;
}

export function fetchWalks(search: string): Promise<WalksResponse> {
  return getJson<WalksResponse>(`/api/walks${search}`);
}

// ---------------------------------------------------------------------------
// GET /api/projects
// ---------------------------------------------------------------------------

/** One tile in Home's project strip (design 16b). */
export interface ProjectCardRow {
  id: string;
  /** `""` for a site with no name — the client decides what to print. */
  name: string;
  /** Where this project's records go. Absent is a real answer: a site can be
   *  used with no integration at all. */
  syncSource?: "procore" | "autodesk";
  /** Street address and coordinates — the tile's site map (`SiteMap`). */
  address?: string;
  location?: { lat: number; lng: number };
  /** ISO string — the most recent walk's start, or absent. */
  lastWalkAt?: string;
  toReview: number;
  pastDue: number;
}

export interface ProjectCardsResponse extends WorkspaceEnvelope {
  generatedAt: string;
  projects: ProjectCardRow[];
  totals: { sites: number; toReview: number; pastDue: number };
}

/**
 * The strip's data is CEILING-wide and takes no parameters — it is the
 * control that changes `?site=`, so it must go on showing the way back out.
 * `search` carries the workspace and nothing else that matters here.
 */
export function fetchProjectCards(search: string): Promise<ProjectCardsResponse> {
  return getJson<ProjectCardsResponse>(`/api/projects${search}`);
}

// ---------------------------------------------------------------------------
// GET /api/project-picker
// ---------------------------------------------------------------------------

/**
 * The picker's query string: the page's `workspace` (and nothing else from the
 * page URL — `?site=` must not narrow the list that changes it) plus the
 * picker's own parameters.
 */
function pickerPath(search: string, params: Record<string, string>): string {
  const query = new URLSearchParams();
  const workspace = new URLSearchParams(search).get("workspace");
  if (workspace) query.set("workspace", workspace);
  for (const [key, value] of Object.entries(params)) if (value) query.set(key, value);
  const text = query.toString();
  return `/api/project-picker${text ? `?${text}` : ""}`;
}

/** One page of the picker's server-side search. */
export function fetchProjectPickerPage(
  search: string,
  options: { query: string; offset: number },
): Promise<ProjectPickerPage> {
  return getJson<ProjectPickerPage>(
    pickerPath(search, {
      q: options.query.trim(),
      offset: options.offset > 0 ? String(options.offset) : "",
    }),
  );
}

/** The Recent section's rows, by id, most recent first. */
export function fetchPickerProjectsById(search: string, ids: string[]): Promise<ProjectPickerByIdResponse> {
  return getJson<ProjectPickerByIdResponse>(pickerPath(search, { ids: ids.join(",") }));
}

// ---------------------------------------------------------------------------
// GET /api/projects/:jobSiteId/brief
// ---------------------------------------------------------------------------

/**
 * One project's Brief — deliberately shorter than the workspace one: stats,
 * stalled, walks, items. Mirrors `server/queries/getProjectBrief.ts`.
 *
 * `stalled` is a filter of `items`, in the same order, because the server
 * derived both from one read. A view must not re-derive either.
 */
export interface ProjectBriefResponse extends WorkspaceEnvelope {
  generatedAt: string;
  jobSiteId: string;
  siteName?: string;
  stats: {
    openItems: number;
    stalled: number;
    records: number;
    walks: number;
    stallThresholdDays: number;
    /** ISO string, or absent when the project has never been walked. */
    lastWalkAt?: string;
  };
  stalled: ConsoleRow[];
  items: ConsoleRow[];
  walks: ConsoleWalkResponse[];
  walkTotal: number;
  walkLimit: number;
}

export function fetchProjectBrief(
  jobSiteId: string,
  search: string,
): Promise<ProjectBriefResponse> {
  return getJson<ProjectBriefResponse>(
    `/api/projects/${encodeURIComponent(jobSiteId)}/brief${search}`,
  );
}

// ---------------------------------------------------------------------------
// Plan 6's reads
// ---------------------------------------------------------------------------

export function fetchPeople(search: string): Promise<PeopleResponse> {
  return getJson<PeopleResponse>(`/api/people${search}`);
}

// ---------------------------------------------------------------------------
// The Procore baseline dashboard (Led AI internal)
// ---------------------------------------------------------------------------

export type {
  ProcoreBaselineResponse,
  ProcoreBaselineSetup,
  ProcoreBaselineSchedule,
  ProcoreBaselineCohortMember,
  ProcoreBaselinePull,
  ProcoreBaselineProject,
  ProcoreBaselineUserRow,
  ProcoreBaselineWindow,
  ProcoreBaselineCounts,
} from "../../../backend/src/shared/procoreBaseline";

/** The console route's envelope around the backend's response. */
export interface ProcoreBaselineEnvelope extends WorkspaceEnvelope {
  ok: boolean;
  result?: import("../../../backend/src/shared/procoreBaseline").ProcoreBaselineResponse;
  configured?: boolean;
  supported?: boolean;
  error?: string;
}

/**
 * The stored pull for the active team workspace — a read of what the LAST
 * press of the button produced, never a call to Procore. `pullId` opens an
 * earlier pull from the history list.
 */
export function fetchProcoreBaseline(search: string, pullId?: string): Promise<ProcoreBaselineEnvelope> {
  const params = new URLSearchParams(search);
  if (pullId) params.set("pullId", pullId);
  const qs = params.toString();
  return getJson<ProcoreBaselineEnvelope>(`/api/procore-baseline${qs ? `?${qs}` : ""}`);
}

/** The console route's envelope around one directory page. */
export interface ProcoreDirectoryEnvelope extends WorkspaceEnvelope {
  ok: boolean;
  result?: import("../../../backend/src/shared/procoreBaseline").ProcoreDirectoryPage;
  error?: string;
}

/**
 * One page of a read project's directory for the people picker — filtered
 * and ordered on the backend from the stored pull, never a call to Procore.
 * `cursor` is the previous page's `nextCursor`.
 */
export function fetchProcoreBaselineDirectory(
  search: string,
  options: { project: string; q?: string; cursor?: string | null; limit?: number },
): Promise<ProcoreDirectoryEnvelope> {
  const params = new URLSearchParams(search);
  params.set("project", options.project);
  if (options.q?.trim()) params.set("q", options.q.trim());
  if (options.cursor) params.set("cursor", options.cursor);
  if (options.limit) params.set("limit", String(options.limit));
  return getJson<ProcoreDirectoryEnvelope>(`/api/procore-baseline/directory?${params.toString()}`);
}

/** Saves the one-time setup: the pilot start date and/or the chosen people.
 *  Fields left out keep their stored value. */
export function saveProcoreBaselineSetup(
  search: string,
  patch: {
    pilotStartDate?: string | null;
    cohort?: import("../../../backend/src/shared/procoreBaseline").ProcoreBaselineCohortMember[];
    /** `null` restores the default checkpoint calendar. */
    schedule?: import("../../../backend/src/shared/procoreBaseline").ProcoreBaselineSchedule | null;
  },
): Promise<MutationResult<{ setup: import("../../../backend/src/shared/procoreBaseline").ProcoreBaselineSetup }>> {
  return postMutation("/api/procore-baseline/setup", search, patch);
}

/** The button. Starts a pull against the customer's Procore; 202 with the
 *  queued pull, 409 while one is already running. */
export function startProcoreBaselinePull(
  search: string,
  pilotStartDate: string | null,
): Promise<MutationResult<{ pullId: string }>> {
  return postMutation("/api/procore-baseline/pull", search, pilotStartDate ? { pilotStartDate } : {});
}

/**
 * One member's usage. `search` rides along so the request answers for the same
 * workspace the page behind it claims to be showing — and so `?withinDays=`
 * reaches the same validator `/api/people` uses.
 *
 * The id is encoded rather than interpolated raw: it arrives from a URL
 * parameter, and a `/` or `?` in it would silently address a different route.
 */
export function fetchMemberUsage(
  userId: string,
  search: string,
): Promise<MemberUsageResponse> {
  return getJson<MemberUsageResponse>(
    `/api/people/${encodeURIComponent(userId)}${search}`,
  );
}

export function fetchPolicies(search: string): Promise<PoliciesResponse> {
  return getJson<PoliciesResponse>(`/api/policies${search}`);
}

export function fetchInvite(search: string): Promise<InviteResponse> {
  return getJson<InviteResponse>(`/api/invite${search}`);
}

// ---------------------------------------------------------------------------
// GET/POST/DELETE /api/team-join-link — the shareable team join link
// (spec: 2026-09-24-member-join-links-spec.md).
// ---------------------------------------------------------------------------

/** A row from `backend`'s `TeamJoinLink` model. Rounded to just the fields
 *  the console renders. `role` is pinned to `"member"` at the model — the
 *  string type is what the wire carries, not permission for it to widen. */
export interface JoinLinkRow {
  token: string;
  orgId: string;
  role: "member";
  createdBy: string;
  /** ISO string. */
  expiresAt: string;
  active: boolean;
  /** ISO string. */
  createdAt: string;
}

/**
 * The active join link, or `null` if none / expired / deactivated, plus an
 * `unavailable` field when the backend could not be reached. Follows the
 * same "empty vs. failure" discipline `AskHistory` uses — the two are not
 * the same fact and must not read as one.
 */
export interface JoinLinkResource {
  link: JoinLinkRow | null;
  unavailable?: string;
}

/**
 * READ the org's current active join link.
 *
 * The console route forwards to `backend`'s `GET /api/team-join-link` with
 * the caller's own bearer, and wraps the response in the same envelope
 * every forward uses (`{ ok, result, ... }`). Failure is REPORTED — an
 * unavailable backend or a supported-but-refused call becomes an
 * `unavailable` string, never silently an "empty" link.
 */
export async function fetchJoinLink(search: string): Promise<JoinLinkResource> {
  try {
    const body = await getJson<{
      ok?: boolean;
      error?: string;
      result?: { link?: JoinLinkRow | null };
    }>(`/api/team-join-link${search}`);
    if (body.ok === false) {
      return {
        link: null,
        unavailable: body.error ?? "The current join link could not be read.",
      };
    }
    return { link: body.result?.link ?? null };
  } catch (error) {
    return {
      link: null,
      unavailable:
        error instanceof ApiError
          ? error.message
          : "The current join link could not be read from this console.",
    };
  }
}

/** MINT (or return the existing active) join link for the caller's org.
 *  `orgId` composed server-side, not from the browser. */
export function postJoinLinkMint(search: string): Promise<MutationResult<JoinLinkRow>> {
  return postMutation<JoinLinkRow>(`/api/team-join-link`, search, {});
}

/** DEACTIVATE one join link by its token. `search` still carries
 *  `?workspace=` so `orgId` resolves server-side against the same scope. */
export function deleteJoinLinkDeactivate(
  search: string,
  token: string,
): Promise<MutationResult<{ ok: true }>> {
  return deleteMutation<{ ok: true }>(
    `/api/team-join-link/${encodeURIComponent(token)}`,
    search,
  );
}

/** ⌘K. `search` carries the console URL so the palette can never answer for
 *  a different workspace than the page behind it. */
export function fetchSearch(
  search: string,
  query: string,
): Promise<SearchResponse> {
  const params = new URLSearchParams(search);
  params.set("q", query);
  return getJson<SearchResponse>(`/api/search?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// The console's ONE mutation call
// ---------------------------------------------------------------------------

/**
 * The result of asking the console to change something.
 *
 * `configured: false` means this deployment has no backend to send the change
 * TO. It is deliberately its own field rather than folded into the message:
 * the views disable their controls on it up front, so the user never presses
 * a button that could not have worked.
 */
export interface MutationResult<T = unknown> {
  ok: boolean;
  status: number;
  configured: boolean;
  /**
   * False when the backend IS configured and reachable but has no such route
   * — a deployment older than the capability being asked for. Its own field
   * for the same reason `configured` is: it is a different problem, with a
   * different fix, from a refusal. `true` whenever nothing says otherwise.
   */
  supported: boolean;
  result?: T;
  error?: string;
  /** The server's clock when it answered (its `Date` header), in ms. */
  serverAt?: number;
}

/**
 * POST to one of the console's mutation routes.
 *
 * The console does not write: the route forwards this to `backend/` over
 * HTTPS with the caller's own credentials (see `server/backendGateway.ts`).
 * This function therefore does NOT throw on a refusal — a 403 from the
 * backend's admin gate is information the Policies page must print next to
 * the control that was refused, not an exception that replaces the page.
 */
/** What a review decision may be. The same four `backend`'s review route
 *  accepts; the queue's three buttons cover the first three, and `deferred`
 *  is set on the phone, never here. */
export type ReviewDecision = "approved" | "edited" | "rejected" | "deferred";

/**
 * Decide one record — the console's ONE review write.
 *
 * This is the "one function" every disabled control was left waiting on: the
 * row already carries `{ kind, itemId }`, which IS the path. `patch` is the
 * per-kind edit and is sent only for an `edited` decision; `title` is the one
 * key all four kinds accept, which is why the inline editor edits the title
 * and nothing else (see `reviewDecisions.tsx`).
 *
 * Does NOT throw on a refusal, for the reason `postMutation` gives: a 403 or
 * a 404 belongs next to the control that was refused, not in place of the
 * page the reader is on.
 */
export function postReviewDecision(
  kind: ConsoleItemKind,
  itemId: string,
  search: string,
  decision: ReviewDecision,
  patch?: Record<string, unknown>,
  /** Rejection-reason banner sampling report (rejections only) — rides the
   *  decision so the decided event logs the probability in force. */
  reasonPrompt?: { p: number; shown: boolean },
  /** This decision's identity. The backend's Undo receipts key on it, and a
   *  decision on a record that was undone is refused without one. */
  operationId: string = newOperationId(),
): Promise<MutationResult> {
  return postMutation(
    `/api/review/${encodeURIComponent(kind)}/${encodeURIComponent(itemId)}`,
    search,
    {
      decision,
      operationId,
      ...(patch ? { patch } : {}),
      ...(reasonPrompt ? { reasonPrompt } : {}),
    },
  );
}

function newOperationId(): string {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `console-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** The rejection-reason banner's outcome (reject-v1). `backend` emits the
 *  `review_reject_reason` usage event under this caller's identity. */
export function postRejectReason(
  search: string,
  body: {
    itemId: string;
    kind: ConsoleItemKind;
    response: "selected" | "dismissed" | "timeout";
    reason: "not_said" | "not_worth_logging" | "already_covered" | "other" | null;
    freeText?: string;
    jobSiteId?: string;
    visitId?: string;
  },
): Promise<MutationResult> {
  return postMutation("/api/review/reject-reason", search, body);
}

/** Save an accepted item's mobile-schema edit. The browser keeps the console
 * kind vocabulary; the backend translates it before invoking the mobile
 * JobSiteManager, so the phone and web share one write implementation. */
export function postItemEdit(
  kind: ConsoleItemKind,
  itemId: string,
  search: string,
  patch: Record<string, unknown>,
): Promise<MutationResult> {
  return postMutation(
    `/api/items/${encodeURIComponent(kind)}/${encodeURIComponent(itemId)}/edit`,
    search,
    { patch },
  );
}

/** Delete one reviewed entry — the phone's `deleteReviewedItem` (a synced
 *  record is tombstoned and the delete mirrored to Procore). */
export function postItemDelete(
  kind: ConsoleItemKind,
  itemId: string,
  search: string,
): Promise<MutationResult> {
  return postMutation(
    `/api/items/${encodeURIComponent(kind)}/${encodeURIComponent(itemId)}/delete`,
    search,
    {},
  );
}

/** What `backend` answers a console create with — see
 *  `backend/src/backend/services/consoleItemCreate.service.ts`. */
export interface ItemCreateResult {
  ok: true;
  kind: "punch" | "observation";
  itemId: string;
  sync: {
    provider: "procore" | null;
    status: string;
    /** Punch: Procore's workflow status after the send, as Procore reported it. */
    workflowStatus?: string;
    sentToAssignee: boolean;
    error?: string;
  };
  existing?: boolean;
  photoErrors?: string[];
}

export interface ItemCreateBody {
  jobSiteId: string;
  title: string;
  description?: string;
  priority: "low" | "medium" | "high";
  dueDate?: string;
  selectedAssigneeRemoteId?: string;
  selectedAssigneeName?: string;
  selectedLocationRemoteId?: string;
  selectedLocationName?: string;
  selectedTradeRemoteId?: string;
  selectedTradeName?: string;
  observationType?: string;
  sendToAssignee?: boolean;
  photos?: Array<{ base64Data: string; mimeType: string }>;
  operationId: string;
}

/** Create one punch item or observation outside a walk. `backend` saves it
 *  reviewed and approved, publishes it to Procore when the project is linked,
 *  and — for a punch item with an assignee and Send on — sends it to the
 *  assignee so Procore moves it to Work Required. A retried request with the
 *  same `operationId` returns the record already made. */
export function postItemCreate(
  kind: "punch" | "observation",
  search: string,
  body: ItemCreateBody,
): Promise<MutationResult<ItemCreateResult>> {
  return postMutation<ItemCreateResult>(
    `/api/items/${encodeURIComponent(kind)}/create`,
    search,
    body as unknown as Record<string, unknown>,
  );
}

export function newCreateOperationId(): string {
  return newOperationId();
}

/** Re-push one record's failed Procore or ACC sync — the phone's Retry sync
 * (`retryReviewedItemSync`), owner-only. A refusal comes back as `error`. */
export function postItemRetrySync(
  kind: ConsoleItemKind,
  itemId: string,
  search: string,
): Promise<MutationResult> {
  return postMutation(
    `/api/items/${encodeURIComponent(kind)}/${encodeURIComponent(itemId)}/retry-sync`,
    search,
    {},
  );
}

export async function postMutation<T = unknown>(
  path: string,
  search: string,
  body: Record<string, unknown>,
): Promise<MutationResult<T>> {
  try {
    const response = await fetch(consoleUrl(`${path}${search}`), {
      method: "POST",
      headers: {
        ...(await authHeaders()),
        "content-type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify(body),
    });
    const parsed = (await response.json().catch(() => ({}))) as {
      ok?: boolean;
      configured?: boolean;
      supported?: boolean;
      error?: string;
      result?: T;
    };
    if (response.ok && parsed.ok !== false && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(DATA_CHANGED_EVENT, { detail: {
        purge: /\/api\/(review|members|policies|invitations)(?:\/|$)/.test(path),
        path,
      } }));
    }
    const serverAt = Date.parse(response.headers.get("date") ?? "");
    return {
      ok: response.ok && parsed.ok !== false,
      status: response.status,
      configured: parsed.configured ?? response.ok,
      supported: parsed.supported ?? true,
      ...(parsed.result !== undefined ? { result: parsed.result } : {}),
      ...(parsed.error ? { error: parsed.error } : {}),
      ...(Number.isFinite(serverAt) ? { serverAt } : {}),
    };
  } catch (error) {
    return {
      ok: false,
      status: 0,
      configured: true,
      supported: true,
      error:
        (error as Error).message || "The console could not reach the server.",
    };
  }
}

/**
 * PATCH to one of the console's mutation routes — mirrors `postMutation`
 * exactly (same envelope, same non-throwing discipline on a refusal), with
 * the method swapped for `handleMethodMutation`'s PATCH routes (currently
 * just `/api/members/:userId`'s role change). `search` carries the
 * `?workspace=` scope on the URL, the SAME way every fetcher and
 * `postMutation` already do — never as a header or body field.
 */
/** One comment on a walk photo, as `getPhotoComments` shapes it. */
export interface PhotoCommentResponse {
  id: string;
  authorId: string;
  authorName?: string;
  /** True when the caller wrote it. */
  mine: boolean;
  /** ISO string. */
  at: string;
  text: string;
}

export interface PhotoCommentsResponse {
  comments: PhotoCommentResponse[];
}

export function fetchPhotoComments(
  visitId: string,
  photoId: string,
  search: string,
): Promise<PhotoCommentsResponse> {
  return getJson<PhotoCommentsResponse>(
    `/api/walks/${encodeURIComponent(visitId)}/photos/${encodeURIComponent(photoId)}/comments${search}`,
  );
}

export function addPhotoComment(
  visitId: string,
  photoId: string,
  search: string,
  text: string,
): Promise<MutationResult> {
  return postMutation(
    `/api/walks/${encodeURIComponent(visitId)}/photos/${encodeURIComponent(photoId)}/comments`,
    search,
    { text },
  );
}

/** The photo viewer's description edit — lands on the mobile manager's own
 *  `updatePhotoCaption` (owner-only; the description shown everywhere becomes
 *  this text, and a Procore-synced image is PATCHed best-effort). */
export function updateWalkPhotoDescription(
  visitId: string,
  photoId: string,
  search: string,
  description: string,
): Promise<MutationResult> {
  return patchMutation(
    `/api/walks/${encodeURIComponent(visitId)}/photos/${encodeURIComponent(photoId)}/description`,
    search,
    { description },
  );
}

export async function patchMutation<T = unknown>(
  path: string,
  search: string,
  body: Record<string, unknown>,
): Promise<MutationResult<T>> {
  try {
    const response = await fetch(consoleUrl(`${path}${search}`), {
      method: "PATCH",
      headers: {
        ...(await authHeaders()),
        "content-type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify(body),
    });
    const parsed = (await response.json().catch(() => ({}))) as {
      ok?: boolean;
      configured?: boolean;
      supported?: boolean;
      error?: string;
      result?: T;
    };
    if (response.ok && parsed.ok !== false && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(DATA_CHANGED_EVENT, { detail: {
        purge: /\/api\/(review|members|policies|invitations)(?:\/|$)/.test(path),
        path,
      } }));
    }
    return {
      ok: response.ok && parsed.ok !== false,
      status: response.status,
      configured: parsed.configured ?? response.ok,
      supported: parsed.supported ?? true,
      ...(parsed.result !== undefined ? { result: parsed.result } : {}),
      ...(parsed.error ? { error: parsed.error } : {}),
    };
  } catch (error) {
    return {
      ok: false,
      status: 0,
      configured: true,
      supported: true,
      error:
        (error as Error).message || "The console could not reach the server.",
    };
  }
}

/**
 * DELETE to one of the console's mutation routes — mirrors `postMutation`
 * exactly, minus a body: `handleMethodMutation`'s DELETE routes
 * (`/api/members/:userId`, `/api/invitations/:id`) read `orgId` off the
 * QUERY STRING rather than a JSON body (see `handleMethodMutation`'s own
 * doc), and a browser `DELETE` sends no body to begin with. `search` still
 * carries the `?workspace=` scope on the URL, the same way every other
 * mutation call does.
 */
export async function deleteMutation<T = unknown>(
  path: string,
  search: string,
): Promise<MutationResult<T>> {
  try {
    const response = await fetch(consoleUrl(`${path}${search}`), {
      method: "DELETE",
      headers: {
        ...(await authHeaders()),
        accept: "application/json",
      },
    });
    const parsed = (await response.json().catch(() => ({}))) as {
      ok?: boolean;
      configured?: boolean;
      supported?: boolean;
      error?: string;
      result?: T;
    };
    if (response.ok && parsed.ok !== false && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(DATA_CHANGED_EVENT, { detail: { purge: true } }));
    }
    return {
      ok: response.ok && parsed.ok !== false,
      status: response.status,
      configured: parsed.configured ?? response.ok,
      supported: parsed.supported ?? true,
      ...(parsed.result !== undefined ? { result: parsed.result } : {}),
      ...(parsed.error ? { error: parsed.error } : {}),
    };
  } catch (error) {
    return {
      ok: false,
      status: 0,
      configured: true,
      supported: true,
      error:
        (error as Error).message || "The console could not reach the server.",
    };
  }
}

export async function fetchProjectMentions(jobSiteId: string, query: string, search: string): Promise<import("../../../backend/src/shared/askMentions").AskMention[]> {
  const params = new URLSearchParams(search);
  params.set("jobSiteId", jobSiteId);
  params.set("q", query);
  const body = await getJson<{ ok?: boolean; error?: string; result?: { mentions: import("../../../backend/src/shared/askMentions").AskMention[] } }>(`/api/project-mentions?${params}`);
  if (!body.result || body.ok === false) throw new Error(body.error || "Could not search project directory.");
  return body.result.mentions;
}

// ── Walk photos review step ─────────────────────────────────────────────────

/** Which record a walk photo belongs to — `backend/`'s `WalkPhotoHome`. */
export interface WalkPhotoHome {
  kind: "photos" | "log" | "observation" | "task" | "punch";
  itemId: string;
  category?: string;
  title: string;
  approved: boolean;
}

export interface WalkPhotoLocation {
  name: string;
  path?: string;
  procoreLocationId?: string;
  freeText?: boolean;
}

export interface ConsoleWalkPhoto {
  mediaId: string;
  url: string | null;
  thumbnailUrl: string | null;
  type: "image" | "video";
  timestamp: string;
  description: string;
  descriptionPending: boolean;
  location?: WalkPhotoLocation;
  home: WalkPhotoHome | null;
  /** Marked "Not syncing" on the phone's Walk photos (LED-766): kept in Led AI. */
  syncExcluded?: boolean;
  /** Made into this still-undecided record with Make it a (LED-1154). */
  convertedTo?: { recordId: string; recordKind: WalkPhotoConvertKind };
}

export interface WalkPhotosResponse {
  visitId: string;
  jobSiteId: string;
  /** When the walk was (ISO): the card's stamp and "{date} · Photos". */
  walkStartedAt?: string | null;
  /** The reader's role — the default destination, as the phone picks it. */
  userRole?: string | null;
  /** The photo viewer's GENERAL rows. Absent from an older console server. */
  walkName?: string;
  projectName?: string;
  photos: ConsoleWalkPhoto[];
  writesConfigured: boolean;
  writesNote: string;
}

export type WalkPhotoConvertTarget = "task" | "observation" | "punch";
/** What Make it a can make on the console: the phone's three, plus a daily
 *  log entry in a chosen section (`backend/`'s `WalkPhotoConvertKind`). */
export type WalkPhotoConvertKind = WalkPhotoConvertTarget | "dailyLog";
export type WalkPhotoDestination = "log" | "gallery";

export interface WalkPhotoSubmitResult {
  ok: boolean;
  removed: number;
  converted: number;
  approvedCards: number;
  failedMediaIds: string[];
}

export function fetchWalkPhotos(visitId: string, search: string): Promise<WalkPhotosResponse> {
  return getJson<WalkPhotosResponse>(`/api/walks/${encodeURIComponent(visitId)}/walk-photos${search}`);
}

export function updateWalkPhotoLocation(
  visitId: string,
  photoId: string,
  search: string,
  /** `null` removes the photo's location ("No location"). */
  location: WalkPhotoLocation | null,
): Promise<MutationResult> {
  return patchMutation(
    `/api/walks/${encodeURIComponent(visitId)}/photos/${encodeURIComponent(photoId)}/location`,
    search,
    { location },
  );
}

/** Convert to: create the pending record now (the phone's `convertWalkPhoto`);
 *  the review queue then opens it in the record editor. */
export function convertWalkPhoto(
  visitId: string,
  photoId: string,
  search: string,
  targetKind: WalkPhotoConvertKind,
  /** The record's literal starting title ("New observation"), and — for a
   *  daily log entry — its section (`manpower`, `safety_violations`…). */
  options: { title?: string; section?: string } = {},
): Promise<MutationResult<{ ok: boolean; itemId?: string }>> {
  return postMutation<{ ok: boolean; itemId?: string }>(
    `/api/walks/${encodeURIComponent(visitId)}/photos/${encodeURIComponent(photoId)}/convert`,
    search,
    {
      targetKind,
      ...(options.title ? { title: options.title } : {}),
      ...(targetKind === "dailyLog" && options.section ? { section: options.section } : {}),
    },
  );
}

/** Undo a Make it a: the photo goes back on its Photos card and the
 *  undecided record made from it is deleted (`undoWalkPhotoConvert`). */
export function undoWalkPhotoConvert(
  visitId: string,
  search: string,
  recordId: string,
): Promise<MutationResult<{ ok: boolean; mediaId?: string }>> {
  return postMutation<{ ok: boolean; mediaId?: string }>(
    `/api/walks/${encodeURIComponent(visitId)}/convert/undo`,
    search,
    { recordId },
  );
}

/** Send: delete removed photos, create converted records, approve the walk's
 *  Photos cards — the phone's own `submitWalkPhotoReview`. */
export function submitWalkPhotos(
  visitId: string,
  search: string,
  input: {
    removedMediaIds: string[];
    deleteOnly?: boolean;
    conversions: Array<{ mediaId: string; targetKind: WalkPhotoConvertTarget; title?: string }>;
    /** The Photos cards this Send decides (LED-711); absent = every pending one. */
    photoCardIds?: string[];
    /** Left out with MinusCircle: kept in Led AI, not sent (LED-1154). */
    excludedMediaIds?: string[];
    /** Daily log + gallery, or gallery only (design 7). Absent = log. */
    destination?: WalkPhotoDestination;
  },
): Promise<MutationResult<WalkPhotoSubmitResult>> {
  return postMutation<WalkPhotoSubmitResult>(
    `/api/walks/${encodeURIComponent(visitId)}/walk-photos`,
    search,
    input,
  );
}

export function fetchAutodeskReport(visitId: string, search: string): Promise<{ report: AutodeskSavedForm }> {
  return getJson(`/api/walks/${encodeURIComponent(visitId)}/autodesk-report${search}`);
}

export interface AutodeskReportOverridePatch {
  name?: string | null;
  description?: string | null;
  notes?: string | null;
  locationId?: string | null;
  clearLocationOverride?: boolean;
  answer?: { fieldId: string; value: string | string[] | null };
  photo?: { mediaId: string; excluded: boolean };
}

/** A value typed into the daily report itself; the backend keeps it across
 * re-projections and republishes the day's Form. */
export function postAutodeskReportOverride(
  visitId: string,
  search: string,
  patch: AutodeskReportOverridePatch,
): Promise<MutationResult<{ overrides: AutodeskDailyReportOverrides }>> {
  return postMutation(`/api/walks/${encodeURIComponent(visitId)}/autodesk-report/overrides`, search, { patch });
}

/** A new table row — a new, already-reviewed record in the table's category. */
export function postAutodeskReportRow(
  visitId: string,
  search: string,
  body: {
    category: string;
    title?: string;
    fieldValues?: Record<string, string | number | boolean | null>;
    /** The row's company, picked from the Autodesk directory. */
    selectedCompanyRemoteId?: string;
    selectedCompanyName?: string;
  },
): Promise<MutationResult<{ itemId: string }>> {
  return postMutation(`/api/walks/${encodeURIComponent(visitId)}/autodesk-report/rows`, search, body);
}

/** Move a generated record to another daily-log section before review. */
/** Undo a review decision (`backend/`'s restoreReviewItem). A lapsed or
 *  superseded receipt comes back `ok: false` with the backend's reason. */
export function postReviewRestore(
  search: string,
  body: { kind: ConsoleItemKind; itemId: string },
) {
  return postMutation("/api/review/restore", search, body);
}

export function postReviewConvert(
  search: string,
  body: { sourceKind: string; sourceId: string; targetKind: string; targetSubcategory?: string },
): Promise<MutationResult<{ id?: string; item?: unknown }>> {
  return postMutation("/api/review/convert", search, body);
}

// ---------------------------------------------------------------------------
// Project details — the Directory and Locations pages
// ---------------------------------------------------------------------------

export type {
  ProjectDetailsCompany,
  ProjectDetailsCompanyCount,
  ProjectDetailsDirectory,
  ProjectDetailsDirectoryPage,
  ProjectDetailsError,
  ProjectDetailsFreshness,
  ProjectDetailsLocation,
  ProjectDetailsLocationNode,
  ProjectDetailsLocationsPage,
  ProjectDetailsPerson,
  ProjectDetailsSource,
  ProjectDetailsSummary,
  ProjectFileValidation,
} from "../../../backend/src/shared/projectDetails";

/** Only `?workspace=` travels: the project is the path, and nothing else on
 *  the console URL means anything to these routes. */
function projectFilesQuery(search: string, extra?: Record<string, string>): string {
  const params = new URLSearchParams();
  const workspace = new URLSearchParams(search).get("workspace");
  if (workspace) params.set("workspace", workspace);
  for (const [key, value] of Object.entries(extra ?? {})) params.set(key, value);
  return params.size ? `?${params}` : "";
}

/** The rail's counts and the page header. Never costs a provider call. */
export function fetchProjectDetails(
  projectId: string,
  search: string,
): Promise<import("../../../backend/src/shared/projectDetails").ProjectDetailsSummary> {
  return getJson(`/api/project-details/${encodeURIComponent(projectId)}${projectFilesQuery(search)}`);
}

const definedParams = (params: Record<string, string | number | undefined>) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => [k, String(v)]));

/** One page of the directory, searched and filtered on the server. */
export function fetchProjectDirectory(
  projectId: string,
  search: string,
  params: { q?: string; bucket?: string; offset?: number; limit?: number },
): Promise<import("../../../backend/src/shared/projectDetails").ProjectDetailsDirectoryPage> {
  return getJson(`/api/project-details/${encodeURIComponent(projectId)}/directory${projectFilesQuery(search, definedParams(params))}`);
}

/** Children of `parent` (top level without it), a search, or a subtree. */
export function fetchProjectLocations(
  projectId: string,
  search: string,
  params: { parent?: string; q?: string; subtree?: string },
): Promise<import("../../../backend/src/shared/projectDetails").ProjectDetailsLocationsPage> {
  return getJson(`/api/project-details/${encodeURIComponent(projectId)}/locations${projectFilesQuery(search, definedParams(params))}`);
}

/** A rejected upload: the message, plus the row-level problems when the
 *  backend's parser found any. */
export class ProjectFileError extends ApiError {
  constructor(
    status: number,
    message: string,
    readonly issues: Array<{ row?: number; column?: string; message: string }> = [],
  ) {
    super(status, message);
    this.name = "ProjectFileError";
  }
}

async function postProjectFile<T>(projectId: string, search: string, body: FormData, extra?: Record<string, string>): Promise<T> {
  const response = await fetch(
    consoleUrl(`/api/project-files/${encodeURIComponent(projectId)}${projectFilesQuery(search, extra)}`),
    { method: "POST", body, headers: { ...(await authHeaders()), accept: "application/json" } },
  );
  const data = (await response.json().catch(() => ({}))) as { error?: string; issues?: ProjectFileError["issues"]; supported?: boolean };
  if (!response.ok) {
    throw new ProjectFileError(
      response.status,
      data.supported === false
        ? "This needs a newer Led AI server. Try again after the next update."
        : data.error ?? `Upload failed (${response.status})`,
      data.issues ?? [],
    );
  }
  return data as T;
}

/** Parse one CSV on the server and return what is inside it. Saves NOTHING. */
export function validateProjectFile(
  projectId: string,
  search: string,
  kind: "directory" | "locations",
  file: File,
): Promise<import("../../../backend/src/shared/projectDetails").ProjectFileValidation> {
  const body = new FormData();
  body.set(kind, file);
  return postProjectFile(projectId, search, body, { validate: "1" });
}

/** Make `file` the project's directory or location list. */
export function saveProjectFile(
  projectId: string,
  search: string,
  kind: "directory" | "locations",
  file: File,
  configVersion: number,
): Promise<unknown> {
  const body = new FormData();
  body.set("configVersion", String(configVersion));
  body.set("requestId", crypto.randomUUID());
  body.set(kind, file);
  return postProjectFile(projectId, search, body);
}

// ---------------------------------------------------------------------------
// Integrations — the signed-in user's own Procore / Autodesk sign-ins
// ---------------------------------------------------------------------------

export interface ProcoreSignInStatus {
  connected: boolean;
  reconnectRequired: boolean;
  reconnectReason?: string | null;
  companyId?: number | null;
  companyName?: string | null;
  /** Epoch ms. */
  linkedAt?: number | null;
  availableCompanies?: Array<{ id: number; name: string }>;
  needsCompanySelection?: boolean;
}

export interface IntegrationsResponse {
  configured: boolean;
  /** `production` is the console's environment; `sandbox` is shown only as a note. */
  procore: { production: ProcoreSignInStatus; sandbox: ProcoreSignInStatus } | null;
  autodesk: { connected: boolean; reconnectRequired?: boolean; linkedAt?: number } | null;
  procoreError?: string;
  autodeskError?: string;
}

export type IntegrationProvider = "procore" | "autodesk";

export function fetchIntegrations(): Promise<IntegrationsResponse> {
  return getJson<IntegrationsResponse>("/api/integrations");
}

async function integrationRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(consoleUrl(path), {
    ...init,
    headers: { ...(await authHeaders()), accept: "application/json", ...(init.body ? { "content-type": "application/json" } : {}) },
  });
  const data = (await response.json().catch(() => ({}))) as { error?: string; supported?: boolean };
  if (!response.ok) {
    throw new ApiError(
      response.status,
      data.supported === false ? "This needs a newer Led AI server. Try again after the next update." : data.error ?? `Request failed (${response.status})`,
    );
  }
  return data as T;
}

/** Start a browser sign-in. Procore also returns the attempt to watch. */
export function startIntegrationSignIn(
  provider: IntegrationProvider,
  env: "production" | "sandbox" = "production",
): Promise<{ url: string; attemptId?: string; expiresAt?: number }> {
  return integrationRequest(`/api/integrations/${provider}/connect${env === "sandbox" ? "?env=sandbox" : ""}`, { method: "POST" });
}

/** A browser sign-in's single-use attempt — both providers track one. */
export function readSignInAttempt(
  provider: IntegrationProvider,
  attemptId: string,
): Promise<{ status: "pending" | "processing" | "completed" | "failed" | "cancelled" | "expired"; needsCompanySelection?: boolean }> {
  return integrationRequest(`/api/integrations/${provider}/attempt?id=${encodeURIComponent(attemptId)}`);
}

export function cancelSignInAttempt(provider: IntegrationProvider, attemptId: string): Promise<unknown> {
  return integrationRequest(`/api/integrations/${provider}/attempt?id=${encodeURIComponent(attemptId)}`, { method: "DELETE" });
}

export function chooseProcoreCompany(companyId: number, env: "production" | "sandbox" = "production"): Promise<unknown> {
  return integrationRequest("/api/integrations/procore/company", { method: "POST", body: JSON.stringify({ companyId, env }) });
}

export function signOutOfIntegration(provider: IntegrationProvider): Promise<unknown> {
  return integrationRequest(`/api/integrations/${provider}/disconnect`, { method: "POST" });
}
